import { useTranslation } from 'react-i18next';
import { ExternalLink, Link2, Pencil, X } from 'lucide-react';
import { Button } from '../ui/button';
import { FolderLink } from '../../services/api/folderService';

interface LinkMemberItemProps {
    link: FolderLink;
    onEdit?: (link: FolderLink) => void;
    onRemove?: (link: FolderLink) => void;
    removing?: boolean;
}

const hostOf = (url: string) => {
    try {
        return new URL(url).host;
    } catch {
        return url;
    }
};

export const LinkMemberItem = ({
    link,
    onEdit,
    onRemove,
    removing,
}: LinkMemberItemProps) => {
    const { t } = useTranslation();
    const name = link.title || hostOf(link.url);

    return (
        <div className="flex items-center gap-2 pr-3 transition-colors hover:bg-muted">
            <a
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex min-w-0 flex-1 items-center gap-3 py-3 pl-4"
            >
                <Link2
                    aria-hidden="true"
                    className="h-4 w-4 shrink-0 text-subtle-foreground"
                />
                <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium text-foreground">
                        {name}
                    </span>
                    <span className="block truncate text-xs font-medium text-subtle-foreground">
                        {link.title ? hostOf(link.url) : link.url}
                    </span>
                </span>
                <ExternalLink
                    aria-hidden="true"
                    className="h-3.5 w-3.5 shrink-0 text-subtle-foreground"
                />
            </a>
            {onEdit && (
                <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 shrink-0 text-subtle-foreground hover:text-foreground"
                    onClick={() => onEdit(link)}
                    aria-label={t('folders.links.editAria', {
                        title: name,
                    })}
                >
                    <Pencil className="h-3.5 w-3.5" aria-hidden="true" />
                </Button>
            )}
            {onRemove && (
                <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 shrink-0 text-subtle-foreground hover:text-destructive"
                    onClick={() => onRemove(link)}
                    disabled={removing}
                    aria-label={t('folders.links.removeAria', {
                        title: name,
                    })}
                >
                    <X className="h-3.5 w-3.5" aria-hidden="true" />
                </Button>
            )}
        </div>
    );
};
